import {
  ClinicalNote,
  ClinicalNoteType,
  Medication,
  NivelRisco,
  Prontuario,
  ProntuarioMedication,
  ProntuarioMedicationStatus,
  ProntuarioStatus,
  SessionSchedule,
} from "src/apis/prontuario";
import { formatDateToBR } from "src/utils/date";

export const riskLabels: Record<NivelRisco, string> = {
  baixo: "Baixo",
  moderado: "Moderado",
  alto: "Alto",
  critico: "Crítico",
  nao_informado: "Não informado",
};

export const prontuarioStatusLabels: Record<ProntuarioStatus, string> = {
  ativo: "Ativo",
  inativo: "Inativo",
  arquivado: "Arquivado",
};

export const medicationStatusLabels: Record<ProntuarioMedicationStatus, string> = {
  em_uso: "Em uso",
  suspenso: "Suspenso",
  finalizado: "Finalizado",
  uso_eventual: "Uso eventual",
  desconhecido: "Desconhecido",
};

export const noteTypeLabels: Record<ClinicalNoteType, string> = {
  avaliacao_inicial: "Avaliação inicial",
  evolucao: "Evolução",
  retorno: "Retorno",
  crise: "Crise",
  encaminhamento: "Encaminhamento",
  encerramento: "Encerramento",
  observacao: "Observação",
  soap: "SOAP",
};

export const formatEmpty = (value?: unknown): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue || normalizedValue.toLowerCase() === "null") {
    return "-";
  }

  return normalizedValue;
};

export const getProntuarioPatientName = (prontuario?: Prontuario | null): string => {
  return formatEmpty(
    prontuario?.colaborador?.nome ??
      prontuario?.colaborador_nome ??
      prontuario?.nome
  );
};

export const getProntuarioCompanyName = (prontuario?: Prontuario | null): string => {
  return formatEmpty(
    prontuario?.empresa?.trade_name ??
      prontuario?.empresa?.nome ??
      prontuario?.empresa?.social_reason ??
      prontuario?.empresa_nome
  );
};

export const getMedicationObject = (
  medication: Medication | ProntuarioMedication
): Medication | ProntuarioMedication => {
  return (
    (medication as ProntuarioMedication).medication ??
    (medication as ProntuarioMedication).medicamento ??
    medication
  );
};

export const getMedicationId = (medication?: Medication | null): string => {
  return String(medication?.id ?? medication?.medication_id ?? "");
};

export const getMedicationName = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(item.nome ?? item.name);
};

export const getMedicationActiveIngredient = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(item.principio_ativo ?? item.active_ingredient);
};

export const getMedicationConcentration = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(item.concentracao ?? item.concentration);
};

export const getMedicationForm = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(item.forma_farmaceutica ?? item.pharmaceutical_form);
};

export const getMedicationRoute = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(
    (medication as ProntuarioMedication).usage_route ??
      item.via ??
      item.route
  );
};

export const getMedicationPatientLeafletUrl = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(
    item.bula_paciente_url ??
      item.patient_leaflet_url ??
      (medication as ProntuarioMedication).bula_paciente_url ??
      (medication as ProntuarioMedication).patient_leaflet_url
  );
};

export const getMedicationProfessionalLeafletUrl = (
  medication: Medication | ProntuarioMedication
): string => {
  const item = getMedicationObject(medication);

  return formatEmpty(
    item.bula_profissional_url ??
      item.professional_leaflet_url ??
      (medication as ProntuarioMedication).bula_profissional_url ??
      (medication as ProntuarioMedication).professional_leaflet_url
  );
};

export const getMedicationImageUrls = (
  medication: Medication | ProntuarioMedication
): string[] => {
  const item = getMedicationObject(medication);
  const rawValue =
    item.bula_image_urls ?? (medication as ProntuarioMedication).bula_image_urls;

  if (Array.isArray(rawValue)) {
    return rawValue.filter(Boolean).map(String);
  }

  if (typeof rawValue === "string" && rawValue.trim()) {
    try {
      const parsedValue = JSON.parse(rawValue);

      if (Array.isArray(parsedValue)) {
        return parsedValue.filter(Boolean).map(String);
      }
    } catch (ignored) {
      return rawValue
        .split(",")
        .map((url) => url.trim())
        .filter(Boolean);
    }
  }

  return [];
};

export const getMedicationLinkId = (medication: ProntuarioMedication): string => {
  return String(
    medication.link_id ??
      medication.id ??
      medication.prontuario_medication_id ??
      ""
  );
};

export const formatClinicalDate = (value?: string | null): string => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return formatDateToBR(value);
  }

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const toDatetimeLocalValue = (value?: string | null): string => {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const pad = (number: number) => String(number).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

export const toIsoWithTimezone = (datetimeLocal: string): string => {
  if (!datetimeLocal) {
    return "";
  }

  const timezoneOffset = -new Date(datetimeLocal).getTimezoneOffset();
  const sign = timezoneOffset >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(timezoneOffset);
  const hours = String(Math.floor(absoluteOffset / 60)).padStart(2, "0");
  const minutes = String(absoluteOffset % 60).padStart(2, "0");
  const secondsSuffix = datetimeLocal.length === 16 ? ":00" : "";

  return `${datetimeLocal}${secondsSuffix}${sign}${hours}:${minutes}`;
};

export const getClinicalNoteId = (note: ClinicalNote): string => {
  return String(note.id ?? note.note_id ?? note.anotacao_id ?? "");
};

export const consultaStatusLabels: Record<string, string> = {
  marcada: "Agendada",
  realizada: "Realizada",
  paciente_faltou: "Paciente faltou",
  cancelada: "Cancelada",
};

const toNullableId = (value?: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

export const getSessionServiceId = (session: SessionSchedule): number | null => {
  return toNullableId(session.service_id ?? session.fk_service_id);
};

export const getSessionScheduleId = (session: SessionSchedule): number | null => {
  return toNullableId(
    session.schedule_id ?? session.fk_session_schedule_id ?? session.id
  );
};

/**
 * Um atendimento avulso não tem agendamento, então o id do agendamento não serve
 * como chave do dropdown. O identificador é sempre "service:<id>" ou "schedule:<id>".
 */
export const getSessionSelectionId = (session: SessionSchedule): string => {
  const explicitId = session.selection_id ?? session.value;

  if (explicitId) {
    return String(explicitId);
  }

  const serviceId = getSessionServiceId(session);

  if (serviceId !== null) {
    return `service:${serviceId}`;
  }

  const scheduleId = getSessionScheduleId(session);

  return scheduleId !== null ? `schedule:${scheduleId}` : "";
};

export const getNoteSelectionId = (note: ClinicalNote): string => {
  const serviceId = toNullableId(note.fk_service_id);

  if (serviceId !== null) {
    return `service:${serviceId}`;
  }

  const scheduleId = toNullableId(note.fk_session_schedule_id);

  return scheduleId !== null ? `schedule:${scheduleId}` : "";
};

export const parseSessionSelectionId = (
  selectionId?: string
): { fk_service_id: number | null; fk_session_schedule_id: number | null } => {
  const [sourceType, rawId] = String(selectionId ?? "").split(":");
  const parsedId = toNullableId(rawId);

  if (parsedId === null) {
    return { fk_service_id: null, fk_session_schedule_id: null };
  }

  return sourceType === "service"
    ? { fk_service_id: parsedId, fk_session_schedule_id: null }
    : { fk_service_id: null, fk_session_schedule_id: parsedId };
};

export const getSessionDate = (session: SessionSchedule): string | null => {
  return (
    session.data_realizacao ??
    session.date_of_service ??
    session.scheduled_start_at ??
    null
  );
};

export const formatSessionDate = (value?: string | null): string => {
  if (!value) {
    return "-";
  }

  return String(value).trim().length === 10
    ? formatDateToBR(value)
    : formatClinicalDate(value);
};

export const formatSessionLabel = (session: SessionSchedule): string => {
  const statusLabel =
    consultaStatusLabels[String(session.status_consulta ?? "")] ??
    (getSessionServiceId(session) !== null ? "Atendimento" : "Agendada");
  const date = formatSessionDate(getSessionDate(session));
  const title = formatEmpty(session.title);

  return title === "-"
    ? `${date} · ${statusLabel}`
    : `${date} · ${statusLabel} — ${title}`;
};

export const getStatusColor = (
  status?: string
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  if (status === "critico" || status === "alto" || status === "deletada") {
    return "error";
  }

  if (status === "moderado" || status === "suspenso") {
    return "warning";
  }

  if (status === "baixo" || status === "ativo" || status === "em_uso") {
    return "success";
  }

  if (status === "arquivado" || status === "finalizado") {
    return "default";
  }

  return "info";
};
